/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Grid, IconButton, LinearProgress, Paper, Snackbar, SnackbarCloseReason,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { VTextField, VForm, useVForm, IVFormErrors, AutoCompleteParceiro, VTextFieldData, VFieldMoney, AutoCompletePlanoDeContas, } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { ContasReceberService, IContaConsolidadaFilho, IRecebidoHistorico } from '../../../shared/services/api/Financeiro/ContasReceber';
import { IComprovanteBaixaDados } from './ComprovanteBaixa';
import { DialogVisualizarComprovante } from './DialogVisualizarComprovante';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';

interface IFormData {
  idparceiro: string;
  idplanoconta: string;
  vencimento: Date;
  emissao: Date;
  parcela: string;
  numero: string;
  venda: number;
  vparcela: number;
  vrecebido: number;
  acrescimo: number;
  juros: number;
  desconto: number;
  vreceber: number;
  historico: string;
}
const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  idparceiro: yup.string().required(),
  idplanoconta: yup.string().required(),
  emissao: yup.date().required(),
  vencimento: yup.date().required('O campo é obrigatório')
    .when('$isEditMode', {
      is: false,
      then: () => yup.date().test('valid-date', 'Não se Lança conta já Vencida', function (value) {
        if (!value) {
          return this.createError({ message: 'Data inválida' });
        }
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() - 5);
        return value >= currentDate;
      }),
    }),
  numero: yup.string().default(''),
  parcela: yup.string().required(),
  venda: yup.number().required(),
  vparcela: yup.number().required().test('valorparcela', 'o valor deve ser menor ou igual ao valor total e diferente de zero', function (value, context) {
    const valorcompra = context.parent.venda || 0;
    return value <= valorcompra && value !== 0;
  }),
  vrecebido: yup.number().required().default(0),
  acrescimo: yup.number().required(),
  juros: yup.number().required().default(0),
  desconto: yup.number().required(),
  vreceber: yup.number().required(),
  historico: yup.string().optional().default(''),
});

export const DetalheDeReceber: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const { formRef, save, saveAndClose} = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [descricao, setDescricao] = useState('');
  const [statusVenda, setStatusVenda] = useState(false);
  const [statusRecebidas, setStatusRecebidas] = useState(false);
  const [idparceiro, setIdParceiro] = useState('');
  const [recebidas, setRecebidas] = useState(0);
  const [filhos, setFilhos] = useState<IContaConsolidadaFilho[]>([]);
  const [recebidosHistorico, setRecebidosHistorico] = useState<IRecebidoHistorico[]>([]);
  const [comprovanteDados, setComprovanteDados] = useState<IComprovanteBaixaDados | null>(null);
  const [openComprovante, setOpenComprovante] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [openConfirm, setOpenConfirm] = useState(false);

  // Reconstrói os dados do comprovante a partir de uma linha do histórico
  // de recebimentos — sem depender de nenhum estado guardado no momento da
  // baixa original, já que estamos reabrindo o título bem depois.
  const handleReimprimir = (item: IRecebidoHistorico) => {
    setComprovanteDados({
      parceiro: item.nomeParceiro || '-',
      contaNome: item.nomeConta || '-',
      datalan: item.dataoperacao,
      contas: [{ numero: item.numero || '', valor: Number(item.valor) }],
      // Nem toda baixa antiga guarda o detalhamento por forma de pagamento
      // (só quando foi parcial, ou total com "Juntar Lançamentos" marcado —
      // ver GetRecebidos.ts no backend); sem isso, cai num resumo genérico
      // com só o valor total recebido.
      formasPagamento: item.formasPagamento.length > 0
        ? item.formasPagamento.map((f) => ({ nome: f.historico, valor: Number(f.valor) }))
        : [{ nome: 'Recebimento', valor: Number(item.valor) }],
      totalBaixado: Number(item.valor),
      desconto: 0,
      acrescimo: 0,
    });
    setOpenComprovante(true);
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      navigate('/contasreceber');
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    ContasReceberService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };
  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };
  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ContasReceberService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setDescricao('Lançamento Contas Receber');
            const dataToForm = {
              ...result,
              numero: result.numero === null ? '' : result.numero
            };
            formRef.current?.setData(dataToForm);
            setIdParceiro(result.idparceiro);
            setRecebidas(result.recebido);
            setFilhos(result.filhos || []);
            if (result.tipo == 1) {
              setStatusVenda(true);
            } else {
              setStatusVenda(false);
            }
            if (result.vrecebido > 0) {
              setStatusRecebidas(true);
              ContasReceberService.getRecebidos(id).then((historico) => {
                if (!(historico instanceof Error)) setRecebidosHistorico(historico);
              });
            } else {
              setStatusRecebidas(false);
            }
          }
        });
    } else {
      setFilhos([]);
      setRecebidosHistorico([]);
      formRef.current?.setData({
        emissao: new Date(),
        vencimento: new Date(),
        historico: '',
        numero: '',
        venda: 0,
        valorparcela: 0,
        juros: 0,
        acrescimo: 0,
        desconto: 0,
        vrecebido: 0,
        vreceber: 0,
        vparcela: 0,

      });
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    const validationContext = { isEditMode: id === 'novo' ? false : true };
    formValidationSchema.
      validate(dados, { context: validationContext, abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          ContasReceberService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        } else {
          ContasReceberService
            .updateById(id, { recebido: recebidas, ...dadosValidados })
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handlecalcularParcela = (valor: string | undefined) => {
    if (valor != null) {
      valor = valor.replace(/\D/g, '');
      valor = valor.replace(/(\d)(\d{2})$/, '$1.$2');
      valor = valor.replace(/(?=(\d{3})+(\D))\B/g, '');
      const data = formRef.current?.getData();
      const acrescimo = data?.acrescimo;
      const desconto = data?.desconto;
      const recebido = data?.vrecebido;
      const tparcela = Number(valor);
      const vreceber = tparcela + Number(acrescimo) - Number(desconto) - Number(recebido);
      formRef.current?.setFieldValue('vreceber', vreceber);
    }
  };
  const handlecalcularAcescimo = (valor: string | undefined) => {
    if (valor != null) {
      valor = valor.replace(/\D/g, '');
      valor = valor.replace(/(\d)(\d{2})$/, '$1.$2');
      valor = valor.replace(/(?=(\d{3})+(\D))\B/g, '');
      const data = formRef.current?.getData();
      const vparcela = data?.vparcela;
      const desconto = data?.desconto;
      const recebido = data?.vrecebido;
      const acrescimo = Number(valor);
      const vreceber = acrescimo + Number(vparcela) - Number(desconto) - Number(recebido);
      formRef.current?.setFieldValue('vreceber', vreceber);
    }
  };
  const handlecalcularDesconto = (valor: string | undefined) => {
    if (valor != null) {
      valor = valor.replace(/\D/g, '');
      valor = valor.replace(/(\d)(\d{2})$/, '$1.$2');
      valor = valor.replace(/(?=(\d{3})+(\D))\B/g, '');
      const data = formRef.current?.getData();
      const parcela = data?.vparcela;
      const acrescimo = data?.acrescimo;
      const recebido = data?.vrecebido;
      const desconto = Number(valor);
      const vreceber = Number(parcela) + Number(acrescimo) - desconto - Number(recebido);
      formRef.current?.setFieldValue('vreceber', vreceber);
    }
  };
  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message={id === 'novo' ? 'Salvando lançamento...' : 'Salvando alterações...'}
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Lançamento Contas Receber' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando={isLoading}
          mostrarBotaoImpressao={false}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/contasreceber')}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/contasreceber/novo')}
          mostrarBotaoNovoCarregando={isLoading}
          disableBotaoApagar={statusRecebidas}
          disableBotaoSalvar={isLoading || isSaving}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
                  Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
                  Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={2}>
            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant='h6'>Dados</Typography>
                {filhos.length > 0 && <Chip label="Conta Consolidada" size="small" color="secondary" />}
              </Box>
            </Grid>
            <Grid container item direction="row" spacing={1}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container item direction="row" spacing={1.5} >
                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6} >
                    <AutoCompleteParceiro
                      isExternalLoading={statusVenda || statusRecebidas ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={9} lg={4} xl={4}>
                    <AutoCompletePlanoDeContas
                      isExternalLoading={statusVenda || statusRecebidas ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VTextFieldData
                      fullWidth
                      size='small'
                      name='emissao'
                      label='Emissão'
                      disabled={statusVenda || statusRecebidas ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VTextFieldData
                      fullWidth
                      size='small'
                      name='vencimento'
                      label='Vencimento'
                      disabled={statusVenda ? true : isLoading}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='venda'
                      label='Valor Total'
                      disabled={statusVenda || statusRecebidas ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vparcela'
                      label='Valor Parcela'
                      calcular={handlecalcularParcela}
                      disabled={statusVenda || statusRecebidas ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='parcela'
                      label='Parcelas'
                      disabled={statusVenda || statusRecebidas ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='numero'
                      label='Numero Nota'
                      disabled={statusVenda || statusRecebidas ? true : isLoading}

                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='acrescimo'
                      label='Acréscimo / Juros'
                      calcular={handlecalcularAcescimo}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='desconto'
                      label='Desconto'
                      calcular={handlecalcularDesconto}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='juros'
                      label='juros'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={2} xl={1}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vrecebido'
                      label='Recebido'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={4} xl={1}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vreceber'
                      label='Receber'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <VTextField
                      fullWidth
                      name='historico'
                      multiline
                      rows={3}
                      disabled={isLoading}
                      label='Histório'
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm>
      {filhos.length > 0 && (
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={1}>
            <Grid item>
              <Typography variant='h6'>Parcelas juntadas nessa conta</Typography>
            </Grid>
            <Grid item>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Número</TableCell>
                      <TableCell>Parcela</TableCell>
                      <TableCell align="center">Emissão</TableCell>
                      <TableCell align="center">Vencimento</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                      <TableCell align="right">Valor Parcela</TableCell>
                      <TableCell align="right">Juros/Acré.</TableCell>
                      <TableCell align="right">Recebido</TableCell>
                      <TableCell align="right">Desconto</TableCell>
                      <TableCell align="right">Valor Juntado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filhos.map((filho, index) => (
                      <TableRow key={`${filho.numero}-${index}`}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{filho.numero}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{filho.parcela || '—'}</TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{dayjs(filho.emissao).format('DD/MM/YY')}</TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{dayjs(filho.vencimento).format('DD/MM/YY')}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.venda))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.vparcela))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.acrescimo))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.vrecebido))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(filho.desconto))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatarMoeda(Number(filho.valor))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>
      )}
      {recebidosHistorico.length > 0 && (
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={1}>
            <Grid item>
              <Typography variant='h6'>Comprovantes de pagamento</Typography>
            </Grid>
            <Grid item>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Conta</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="center">Comprovante</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recebidosHistorico.map((item) => (
                      <TableRow key={item.idrecebida}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{dayjs(item.dataoperacao).format('DD/MM/YY HH:mm')}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{item.nomeConta || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatarMoeda(Number(item.valor))}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Reimprimir comprovante">
                            <IconButton size="small" onClick={() => handleReimprimir(item)}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>
      )}
      </LayoutBaseDePagina>
      <DialogVisualizarComprovante
        open={openComprovante}
        dados={comprovanteDados}
        onClose={() => setOpenComprovante(false)}
      />
    </>
  );
};